import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'

export default function Transactions() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [transactions, setTransactions] = useState([])
  const [trusts, setTrusts] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    trust_id: '',
    type: '',
    date_from: '',
    date_to: ''
  })

  useEffect(() => {
    fetchTrusts()
  }, [])

  useEffect(() => {
    fetchTransactions()
  }, [filters])

  const fetchTrusts = async () => {
    const { data } = await supabase
      .from('trust_members')
      .select('trusts(*)')
      .eq('user_id', user.id)
    setTrusts(data?.map(d => d.trusts) || [])
  }

  const fetchTransactions = async () => {
    setLoading(true)

    let query = supabase
      .from('transactions')
      .select('*, accounts(name), trusts(name), beneficiaries(full_name), categories(name)')
      .order('date', { ascending: false })
      .limit(100)

    if (filters.trust_id) query = query.eq('trust_id', filters.trust_id)
    if (filters.type) query = query.eq('type', filters.type)
    if (filters.date_from) query = query.gte('date', filters.date_from)
    if (filters.date_to) query = query.lte('date', filters.date_to)

    const { data, error } = await query
    if (!error) setTransactions(data || [])
    setLoading(false)
  }

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD'
  }).format(amount || 0)

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  })

  const typeColors = {
    income: 'bg-green-100 text-green-700',
    disbursement: 'bg-red-100 text-red-700',
    transfer: 'bg-blue-100 text-blue-700',
    fee: 'bg-orange-100 text-orange-700',
    tax: 'bg-purple-100 text-purple-700',
    corporate_action: 'bg-yellow-100 text-yellow-700',
  }

  const totalIncome = transactions
    .filter(t => t.type === 'income')
    .reduce((s, t) => s + (t.amount || 0), 0)

  const totalDisbursements = transactions
    .filter(t => ['disbursement', 'fee', 'tax'].includes(t.type))
    .reduce((s, t) => s + (t.amount || 0), 0)

  return (
    <Layout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Transactions</h2>
            <p className="text-sm text-gray-500 mt-1">
              {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => navigate('/transactions/new')}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition"
          >
            + Add Transaction
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Total Income</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalIncome)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Total Disbursements & Fees</p>
            <p className="text-2xl font-bold text-red-500 mt-1">{formatCurrency(totalDisbursements)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Net</p>
            <p className={`text-2xl font-bold mt-1 ${totalIncome - totalDisbursements >= 0 ? 'text-gray-900' : 'text-red-500'}`}>
              {formatCurrency(totalIncome - totalDisbursements)}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select
              value={filters.trust_id}
              onChange={e => setFilters({ ...filters, trust_id: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Trusts</option>
              {trusts.map(t => (
                <option key={t?.id} value={t?.id}>{t?.name}</option>
              ))}
            </select>
            <select
              value={filters.type}
              onChange={e => setFilters({ ...filters, type: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="income">Income</option>
              <option value="disbursement">Disbursement</option>
              <option value="transfer">Transfer</option>
              <option value="fee">Fee</option>
              <option value="tax">Tax</option>
              <option value="corporate_action">Corporate Action</option>
            </select>
            <input
              type="date"
              value={filters.date_from}
              onChange={e => setFilters({ ...filters, date_from: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="From date"
            />
            <input
              type="date"
              value={filters.date_to}
              onChange={e => setFilters({ ...filters, date_to: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="To date"
            />
          </div>
        </div>

        {/* Transaction Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-400 text-sm">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-3">↕</p>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No transactions yet</h3>
              <p className="text-sm text-gray-400 mb-6">
                Add your first transaction to start tracking trust activity.
              </p>
              <button
                onClick={() => navigate('/transactions/new')}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition"
              >
                Add First Transaction
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Trust</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Principal</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Income</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Reconciled</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.map((tx) => (
                    <tr
                      key={tx.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/transactions/${tx.id}`)}
                    >
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(tx.date)}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">
                        {tx.description}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {tx.trusts?.name}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${typeColors[tx.type] || 'bg-gray-100 text-gray-600'}`}>
                          {tx.type?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {tx.categories?.name || '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                        {formatCurrency(tx.principal_amount)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                        {formatCurrency(tx.income_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${
                        tx.type === 'income' ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          tx.reconciled
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {tx.reconciled ? '✓ Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}
